/* MedicatiePage — beheer digitale medicatieformulieren.
 *
 * Leiding/pedagogisch: opstellen, verzenden naar ouder, goedkeuren, toediening loggen.
 * Ouder: eigen medicatieformulieren zien + digitaal ondertekenen.
 */
import { useEffect, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Pill, Plus, Send, FileDown, X, PenLine, ThumbsUp, ClipboardCheck, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import StaffPinModal from "@/components/StaffPinModal";

const STATUS_STYLE = {
  concept: { cls: "bg-neutral-100 text-neutral-700", label: "Concept" },
  wacht_ouder: { cls: "bg-amber-100 text-amber-800", label: "Wacht op ouder" },
  ouder_akkoord: { cls: "bg-blue-100 text-blue-800", label: "Ouder akkoord" },
  goedgekeurd: { cls: "bg-emerald-100 text-emerald-800", label: "Goedgekeurd" },
  geweigerd: { cls: "bg-red-100 text-red-800", label: "Geweigerd" },
  verlopen: { cls: "bg-neutral-200 text-neutral-600", label: "Verlopen" },
};

export default function MedicatiePage() {
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [kinderen, setKinderen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [signingId, setSigningId] = useState(null);
  const [dosingId, setDosingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, k] = await Promise.all([
        api.get("/medicatieformulieren"),
        api.get("/children").catch(() => ({ data: [] })),
      ]);
      setItems(m.data.items || []);
      const kk = Array.isArray(k.data) ? k.data : (k.data?.items || []);
      setKinderen(kk);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/auth/me"); setMe(r.data); } catch {}
      await load();
    })();
  }, []);

  const role = (me?.role || "").toLowerCase();
  const isOuder = role === "ouder";
  const canOpstellen = ["superadmin", "platform_admin", "directeur", "locatiemanager", "pedagogisch"].includes(role);
  const canGoedkeuren = ["superadmin", "platform_admin", "directeur", "locatiemanager"].includes(role);

  const verzendOuder = async (mid) => {
    try {
      await api.post(`/medicatieformulieren/${mid}/verzend-ouder`);
      toast.success("Verzonden naar ouder");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
  };

  const goedkeuren = async (mid, beslissing) => {
    try {
      await api.post(`/medicatieformulieren/${mid}/goedkeuren`, { beslissing });
      toast.success(`Formulier ${beslissing}`);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
  };

  return (
    <div className="min-h-screen bg-neutral-50 p-4 md:p-6" data-testid="medicatie-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-display text-neutral-900 flex items-center gap-2">
              <Pill className="h-6 w-6 text-emerald-600" /> Medicatie
            </h1>
            <p className="text-sm text-neutral-600 mt-1">
              {isOuder
                ? "Digitaal toestemming geven voor medicatie voor je kind(eren)."
                : "Digitale medicatieformulieren met ouder-toestemming en toedieningsregistratie."}
            </p>
          </div>
          {canOpstellen && (
            <Button onClick={() => setShowForm(true)} className="text-white"
                    style={{ background: "var(--sage)" }} data-testid="med-new-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Nieuw formulier
            </Button>
          )}
        </div>

        <div className="space-y-3">
          {loading && <div className="text-center py-8 text-sm text-neutral-500">Laden…</div>}
          {!loading && items.length === 0 && (
            <Card className="p-8 text-center bg-white">
              <Pill className="h-8 w-8 mx-auto text-neutral-300 mb-2" />
              <div className="text-sm text-neutral-600">Geen medicatieformulieren.</div>
            </Card>
          )}
          {items.map(m => {
            const s = STATUS_STYLE[m.status] || STATUS_STYLE.concept;
            const isMyKind = isOuder;
            const canSign = isOuder && m.status === "wacht_ouder";
            return (
              <Card key={m.id} className="p-4 bg-white" data-testid={`med-card-${m.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-neutral-900">{m.medicijn_naam}</span>
                    <Badge className={`${s.cls} text-[10px]`} data-testid={`med-status-${m.id}`}>{s.label}</Badge>
                    <span className="text-xs text-neutral-500">voor <strong>{m.kind_naam}</strong></span>
                  </div>
                  <div className="text-xs text-neutral-500 font-mono">
                    {m.start_datum} → {m.eind_datum}
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-neutral-700 mb-2">
                  <div><strong>Reden:</strong> {m.reden}</div>
                  <div><strong>Dosering:</strong> {m.dosering}</div>
                  <div><strong>Wijze:</strong> {m.toediening_wijze}</div>
                  <div><strong>Toedieningen:</strong> {(m.toedieningen || []).length}</div>
                </div>
                {(m.tijdstippen || []).length > 0 && (
                  <div className="text-xs text-neutral-600 mb-2">
                    Tijden: {m.tijdstippen.map(t => `${t.tijd} (${t.dosis})`).join(", ")}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-neutral-100">
                  {m.status === "concept" && canOpstellen && (
                    <Button size="sm" variant="outline" className="h-7 text-xs border-blue-300 text-blue-700"
                            onClick={() => verzendOuder(m.id)} data-testid={`med-send-${m.id}`}>
                      <Send className="h-3 w-3 mr-1" /> Verzenden naar ouder
                    </Button>
                  )}
                  {canSign && (
                    <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => setSigningId(m.id)} data-testid={`med-sign-${m.id}`}>
                      <PenLine className="h-3 w-3 mr-1" /> Digitaal ondertekenen
                    </Button>
                  )}
                  {m.status === "ouder_akkoord" && canGoedkeuren && (
                    <>
                      <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => goedkeuren(m.id, "goedgekeurd")} data-testid={`med-approve-${m.id}`}>
                        <ThumbsUp className="h-3 w-3 mr-1" /> Goedkeuren
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-700"
                              onClick={() => goedkeuren(m.id, "geweigerd")}>
                        Afwijzen
                      </Button>
                    </>
                  )}
                  {m.status === "goedgekeurd" && canOpstellen && (
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                            onClick={() => setDosingId(m.id)} data-testid={`med-dose-${m.id}`}>
                      <ClipboardCheck className="h-3 w-3 mr-1" /> Toediening loggen
                    </Button>
                  )}
                  <a href={`${API}/medicatieformulieren/${m.id}/pdf`} target="_blank" rel="noreferrer"
                     className="ml-auto text-xs text-emerald-700 hover:underline inline-flex items-center gap-1"
                     data-testid={`med-pdf-${m.id}`}>
                    <FileDown className="h-3 w-3" /> PDF
                  </a>
                </div>
                {m.ouder_ondertekend_op && (
                  <div className="mt-2 text-[11px] text-neutral-600 bg-blue-50 border border-blue-200 rounded p-2">
                    Getekend door <strong>{m.ouder_naam}</strong> op {m.ouder_ondertekend_op.slice(0, 10)}
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {showForm && (
          <NewMedicatieModal kinderen={kinderen} onClose={() => setShowForm(false)} onSaved={load} />
        )}
        {signingId && (
          <SignModal formId={signingId} onClose={() => setSigningId(null)} onDone={load} />
        )}
        {dosingId && (
          <DoseModal formId={dosingId} onClose={() => setDosingId(null)} onDone={load} />
        )}
      </div>
    </div>
  );
}

function NewMedicatieModal({ kinderen, onClose, onSaved }) {
  const [form, setForm] = useState({
    kind_id: "", medicijn_naam: "", reden: "", dosering: "",
    toediening_wijze: "oraal", start_datum: new Date().toISOString().slice(0, 10),
    eind_datum: "", bewaarvoorschrift: "", arts_instructie: "",
    allergieen: "", noodinstructies: "", tweede_controle_verplicht: false,
    tijdstippen: [{ tijd: "12:00", dosis: "" }],
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.kind_id || !form.medicijn_naam.trim() || !form.eind_datum) {
      toast.error("Kind, medicijn en einddatum verplicht"); return;
    }
    setBusy(true);
    try {
      await api.post("/medicatieformulieren", form);
      toast.success("Medicatieformulier aangemaakt");
      onSaved(); onClose();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
      setBusy(false);
    }
  };

  const setTijd = (i, field, val) => {
    const arr = [...form.tijdstippen];
    arr[i] = { ...arr[i], [field]: val };
    setForm({ ...form, tijdstippen: arr });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()} data-testid="med-form-modal">
        <div className="p-5 border-b border-neutral-200 flex items-center justify-between">
          <div className="text-lg font-semibold">Nieuw medicatieformulier</div>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <Label>Kind *</Label>
            <Select value={form.kind_id} onValueChange={v => setForm({ ...form, kind_id: v })}>
              <SelectTrigger data-testid="med-kind-select"><SelectValue placeholder="Kies kind" /></SelectTrigger>
              <SelectContent className="bg-white">
                {kinderen.map(k => (
                  <SelectItem key={k.id} value={k.id}>{k.voornaam} {k.achternaam}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Medicijn *</Label>
              <Input value={form.medicijn_naam} onChange={e => setForm({ ...form, medicijn_naam: e.target.value })}
                     data-testid="med-name" /></div>
            <div><Label>Reden *</Label>
              <Input value={form.reden} onChange={e => setForm({ ...form, reden: e.target.value })} /></div>
            <div><Label>Dosering *</Label>
              <Input value={form.dosering} onChange={e => setForm({ ...form, dosering: e.target.value })}
                     placeholder="bv. 5 ml" /></div>
            <div><Label>Toedieningswijze</Label>
              <Select value={form.toediening_wijze} onValueChange={v => setForm({ ...form, toediening_wijze: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="oraal">Oraal</SelectItem>
                  <SelectItem value="druppels">Druppels</SelectItem>
                  <SelectItem value="zetpil">Zetpil</SelectItem>
                  <SelectItem value="inhalatie">Inhalatie</SelectItem>
                  <SelectItem value="huid">Huid (zalf/creme)</SelectItem>
                </SelectContent>
              </Select></div>
            <div><Label>Startdatum</Label>
              <Input type="date" value={form.start_datum}
                     onChange={e => setForm({ ...form, start_datum: e.target.value })} /></div>
            <div><Label>Einddatum *</Label>
              <Input type="date" value={form.eind_datum}
                     onChange={e => setForm({ ...form, eind_datum: e.target.value })} data-testid="med-end" /></div>
          </div>
          <div>
            <Label>Toedieningsmomenten</Label>
            {form.tijdstippen.map((t, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <Input type="time" value={t.tijd} onChange={e => setTijd(i, "tijd", e.target.value)} className="w-32" />
                <Input value={t.dosis} onChange={e => setTijd(i, "dosis", e.target.value)}
                       placeholder="dosis (bv. 5 ml)" />
              </div>
            ))}
            <Button variant="outline" size="sm"
                    onClick={() => setForm({ ...form, tijdstippen: [...form.tijdstippen, { tijd: "18:00", dosis: "" }] })}>
              + Extra moment
            </Button>
          </div>
          <div><Label>Allergieën / risico's</Label>
            <Textarea rows={2} value={form.allergieen} onChange={e => setForm({ ...form, allergieen: e.target.value })} /></div>
          <div><Label>Noodinstructies</Label>
            <Textarea rows={2} value={form.noodinstructies}
                      onChange={e => setForm({ ...form, noodinstructies: e.target.value })} /></div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={form.tweede_controle_verplicht}
                      onCheckedChange={v => setForm({ ...form, tweede_controle_verplicht: v })} />
            Tweede controle verplicht bij toediening
          </label>
        </div>
        <div className="p-5 border-t flex justify-between">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuleren</Button>
          <Button onClick={save} disabled={busy} className="text-white" style={{ background: "var(--sage)" }}
                  data-testid="med-save-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {busy ? "Opslaan…" : "Aanmaken"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SignModal({ formId, onClose, onDone }) {
  const [ouderNaam, setOuderNaam] = useState("");
  const [akkoord, setAkkoord] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!ouderNaam.trim()) { toast.error("Naam is verplicht ter ondertekening"); return; }
    setBusy(true);
    try {
      await api.post(`/medicatieformulieren/${formId}/ouder-tekent`, {
        ouder_naam: ouderNaam.trim(),
        signature_data: `typed:${ouderNaam.trim()}`,
        akkoord,
      });
      toast.success(akkoord ? "Getekend — dank je wel!" : "Geweigerd genoteerd");
      onDone(); onClose();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}
           data-testid="sign-modal">
        <div className="p-5 border-b flex items-center justify-between">
          <div className="text-lg font-semibold flex items-center gap-2">
            <PenLine className="h-5 w-5 text-blue-600" /> Digitaal ondertekenen
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <p className="text-neutral-700">
            Als ouder/verzorger geef je hiermee toestemming voor toediening zoals in het formulier omschreven.
            Deze ondertekening wordt opgeslagen met tijdstempel en jouw account voor de auditlog.
          </p>
          <div>
            <Label>Volledige naam *</Label>
            <Input value={ouderNaam} onChange={e => setOuderNaam(e.target.value)}
                   placeholder="Typ je volledige naam" data-testid="sign-ouder-name" autoFocus />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={akkoord} onCheckedChange={setAkkoord} data-testid="sign-akkoord" />
            Ik ga akkoord met dit medicatieformulier
          </label>
        </div>
        <div className="p-5 border-t flex justify-between">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuleren</Button>
          <Button onClick={submit} disabled={busy || !ouderNaam.trim()}
                  className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="sign-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PenLine className="h-4 w-4 mr-2" />}
            {akkoord ? "Ondertekenen" : "Weigeren"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function DoseModal({ formId, onClose, onDone }) {
  const { user } = useAuth();
  const isGroup = user?.role === "groep_account";
  const now = new Date();
  const [form, setForm] = useState({
    datum: now.toISOString().slice(0, 10),
    tijd: now.toTimeString().slice(0, 5),
    dosis: "", bijzonderheden: "", tweede_controle_door_user_id: "",
  });
  const [busy, setBusy] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinData, setPinData] = useState(null); // {medewerker_user_id, pin, medewerker}

  const doSubmit = async (extra = {}) => {
    if (!form.dosis.trim()) { toast.error("Dosis is verplicht"); return; }
    setBusy(true);
    try {
      await api.post(`/medicatieformulieren/${formId}/toedieningen`, {
        ...form,
        tweede_controle_door_user_id: form.tweede_controle_door_user_id || undefined,
        ...extra,
      });
      toast.success("Toediening geregistreerd");
      onDone(); onClose();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
      setBusy(false);
    }
  };

  const submit = async () => {
    if (isGroup) {
      // Vraag eerst PIN
      setPinOpen(true);
      return;
    }
    await doSubmit();
  };

  const onPinConfirmed = async (data) => {
    setPinData(data);
    await doSubmit({
      performer_medewerker_user_id: data.medewerker_user_id,
      performer_pin: data.pin,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}
           data-testid="dose-modal">
        <div className="p-5 border-b flex items-center justify-between">
          <div className="text-lg font-semibold flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-emerald-600" /> Toediening registreren
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Datum</Label>
              <Input type="date" value={form.datum} onChange={e => setForm({ ...form, datum: e.target.value })} /></div>
            <div><Label>Tijd</Label>
              <Input type="time" value={form.tijd} onChange={e => setForm({ ...form, tijd: e.target.value })} /></div>
          </div>
          <div><Label>Dosis *</Label>
            <Input value={form.dosis} onChange={e => setForm({ ...form, dosis: e.target.value })}
                   placeholder="bv. 5 ml" data-testid="dose-dosis" /></div>
          <div><Label>Bijzonderheden</Label>
            <Textarea rows={2} value={form.bijzonderheden}
                      onChange={e => setForm({ ...form, bijzonderheden: e.target.value })} /></div>
        </div>
        <div className="p-5 border-t flex justify-between">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuleren</Button>
          <Button onClick={submit} disabled={busy || !form.dosis.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dose-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {busy ? "Opslaan…" : (isGroup ? "Registreren met PIN" : "Registreren")}
          </Button>
        </div>
      </div>
      <StaffPinModal
        open={pinOpen}
        onOpenChange={setPinOpen}
        onConfirm={onPinConfirmed}
        title="Wie voert deze toediening uit?"
        description="Groepslogin: identificeer de uitvoerende medewerker."
      />
    </div>
  );
}
